/**
 * Represents the 'selected' device available via {@code useSessionStore}.
 */
export interface Device {
  id: string;
  name: string;
  region: "us-1" | "syd-2" | "uk-1" | "beta-1" | "sit" | "unittest";
  timezone: string;
}

/**
 * Represents the full device config available via {code useDeviceOptionsStore}.
 *
 * NOTE: Currently, only the properties used in Typescript components are listed
 * here, but there are more (see API response); define additional props as needed.
 */
export interface DeviceOptions {
  deviceid?: string;
  display_name?: string;
  display_machine_name?: boolean;
  user_defined_name?: string;
  key?: string;
  uncl_serial?: string;
  content_hub_url?: string;
  timezone?: string;
  allow_extension_connections?: boolean;
  allow_support_school_manager?: boolean;
  allow_support_zendesk?: boolean;
  allow_support_forethought_ai?: boolean;
  parent_id?: string | null;
  is_parent?: boolean;
  customer_internal_company?: string;
  hubspot_company_id?: string;
  enable_extension_chromebooks_only?: boolean;
  student_auto_enrolment?: boolean;
}

export const DeviceOptionsEquals = (o1?: DeviceOptions, o2?: DeviceOptions): boolean => {
  if (!o1 || !o2) {
    return o1 === o2;
  }

  const k1 = Object.keys(o1);
  const k2 = Object.keys(o2);

  if (k1.length === k2.length) {
    for (const k of k1) {
      const tk = k as keyof DeviceOptions;
      if (o1[tk] !== o2[tk]) {
        return false;
      }
    }
    return true;
  }

  return false;
};

/**
 * Usage example:
 * ```
 * export const MyComponent: React.FC = () => {
 *   const [device] = useSessionStore(useCallback((state) => [state.device] as const, []));
 *
 *   if (ShouldUseUSDateFormat(device)) {
 *     return 'US';
 *   }
 *   return 'Not US';
 * };
 * ```
 */
export const ShouldUseUSDateFormat = (d?: Device | DeviceOptions): boolean => {
  return !!d?.timezone && (d.timezone.startsWith("US") || d.timezone.startsWith("America"));
};
